import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trainingApi } from "../services/trainingApi";

const KEYS = {
  scenarios: ["training", "scenarios"] as const,
  sessions: ["training", "sessions"] as const,
  stats: ["training", "stats"] as const,
  goals: ["training", "goals"] as const,
  preferences: ["training", "preferences"] as const,
  session: (id: string) => ["training", "session", id] as const,
};

export function useScenarios() {
  return useQuery({
    queryKey: KEYS.scenarios,
    queryFn: trainingApi.getScenarios,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSessions(limit = 20, offset = 0) {
  return useQuery({
    queryKey: [...KEYS.sessions, limit, offset],
    queryFn: () => trainingApi.getSessions(limit, offset),
  });
}

export function useSession(id: string) {
  return useQuery({
    queryKey: KEYS.session(id),
    queryFn: () => trainingApi.getSession(id),
    enabled: !!id,
  });
}

export function useTrainingStats() {
  return useQuery({
    queryKey: KEYS.stats,
    queryFn: trainingApi.getStats,
    staleTime: 30 * 1000,
  });
}

export function useGoals() {
  return useQuery({
    queryKey: KEYS.goals,
    queryFn: trainingApi.getGoals,
  });
}

export function useStartSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scenarioId: string) => trainingApi.startSession(scenarioId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.sessions });
      queryClient.invalidateQueries({ queryKey: KEYS.stats });
    },
  });
}

export function useEndSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => trainingApi.endSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.sessions });
      queryClient.invalidateQueries({ queryKey: KEYS.stats });
    },
  });
}

export function useTrainingPreferences() {
  return useQuery({
    queryKey: KEYS.preferences,
    queryFn: trainingApi.getPreferences,
    staleTime: 10 * 60 * 1000,
  });
}

export function useSavePreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (prefs: { products: string[]; experience: string; weakAreas: string[] }) =>
      trainingApi.savePreferences(prefs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.preferences });
    },
  });
}

export function useGenerateScenario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (characterId?: string) => trainingApi.generateScenario(characterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.scenarios });
    },
  });
}

export function useSaveScore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, scores }: { sessionId: string; scores: Record<string, any> }) =>
      trainingApi.saveScore(sessionId, scores),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: KEYS.session(vars.sessionId) });
      queryClient.invalidateQueries({ queryKey: KEYS.sessions });
      queryClient.invalidateQueries({ queryKey: KEYS.stats });
    },
  });
}

/** What this agent's REAL dialer numbers say they should practise. */
export function useCallInsights(days = 90) {
  return useQuery({
    queryKey: ["training", "call-insights", days],
    queryFn: () => trainingApi.getCallInsights(days),
    staleTime: 10 * 60 * 1000, // dialer aggregates barely move within a session
  });
}

/** Scenarios an owner has put in front of this agent. */
export function useMyAssignments() {
  return useQuery({
    queryKey: ["training", "assignments", "mine"],
    queryFn: () => trainingApi.getMyAssignments(),
  });
}

/** Owner-only team view. `enabled` must carry the role check — never render for an agent. */
export function useRoster(enabled: boolean, days = 90) {
  return useQuery({
    queryKey: ["training", "roster", days],
    queryFn: () => trainingApi.getRoster(days),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAssignTraining() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: trainingApi.assignTraining,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["training", "roster"] });
      queryClient.invalidateQueries({ queryKey: ["training", "assignments"] });
    },
  });
}

export function useCertifications() {
  return useQuery({ queryKey: ["training", "certifications"], queryFn: () => trainingApi.getCertifications(), staleTime: 5 * 60 * 1000 });
}
/** Owner-only; `enabled` carries the role check. */
export function useRamp(enabled: boolean, months = 6) {
  return useQuery({ queryKey: ["training", "ramp", months], queryFn: () => trainingApi.getRamp(months), enabled, staleTime: 10 * 60 * 1000 });
}
export function useTrainingBilling() {
  return useQuery({ queryKey: ["training", "billing"], queryFn: () => trainingApi.getBilling(), staleTime: 30 * 1000, refetchOnWindowFocus: true });
}
export function useRefundSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, note }: { sessionId: string; note?: string }) => trainingApi.refundSession(sessionId, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["training", "sessions"] });
      void queryClient.invalidateQueries({ queryKey: ["training", "billing"] });
    },
  });
}
export function useLeaderboard(days = 30) {
  return useQuery({ queryKey: ["training", "leaderboard", days], queryFn: () => trainingApi.getLeaderboard(days), staleTime: 2 * 60 * 1000 });
}
export function useBestSessions(enabled: boolean) {
  return useQuery({ queryKey: ["training", "best"], queryFn: () => trainingApi.getBestSessions(), enabled });
}
export function usePeerExamples() {
  return useQuery({ queryKey: ["training", "examples"], queryFn: () => trainingApi.getExamples(), staleTime: 5 * 60 * 1000 });
}
export function useSetExample() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, isExample, note }: { sessionId: string; isExample: boolean; note?: string }) =>
      trainingApi.setExample(sessionId, isExample, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["training", "best"] });
      queryClient.invalidateQueries({ queryKey: ["training", "examples"] });
    },
  });
}
export function useUnderwritingProgress() {
  return useQuery({ queryKey: ["training", "underwriting-progress"], queryFn: () => trainingApi.getUnderwritingProgress(), staleTime: 60 * 1000 });
}
export function useCreateUnderwritingDrill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { category?: string; characterId?: string }) => trainingApi.createUnderwritingDrill(body),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ["training", "scenarios"] }); },
  });
}
export function useQuizHistory() {
  return useQuery({ queryKey: ["training", "quiz", "history"], queryFn: () => trainingApi.getQuizHistory() });
}
export function usePaths(enabled: boolean) {
  return useQuery({ queryKey: ["training", "paths"], queryFn: () => trainingApi.getPaths(), enabled });
}
